import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, Save, Send } from "lucide-react";
import { Btn, FieldRow, Section, cellCls, inputCls } from "@/components/account/Shell";
import { Cell, Dash, PillRow, Sheet, Tag, Tiles, Two, Txt } from "@/components/account/Sheet";
import api, { formatApiError } from "@/lib/api";
import DropoffCode from "@/components/admin/DropoffCode";

import { when } from "@/lib/when";

const ROUTE_COLS = [
    { key: "action", label: "What happens", w: "minmax(180px,1.1fr)" },
    { key: "destination", label: "Goes to", sub: "the shop or the office", w: "minmax(200px,1.3fr)" },
    { key: "customer", label: "Customer gets", w: "minmax(180px,1.1fr)" },
    { key: "owner", label: "You get", w: "minmax(160px,1fr)" },
    { key: "manage", label: "Manage it", sub: "where to change it", w: "minmax(160px,1fr)" },
];
const INBOX_COLS = [
    { key: "shop", label: "Shop", sub: "name · space · status", w: "minmax(200px,1.2fr)" },
    { key: "login", label: "Owner login", sub: "the fallback address", w: "minmax(200px,1.2fr)" },
    { key: "email", label: "Notification email", sub: "blank = the owner's login", w: "minmax(240px,1.4fr)" },
    { key: "actions", label: "Actions", sub: "save", w: "90px" },
];
const LOG_COLS = [
    { key: "when", label: "When", w: "130px" },
    { key: "event", label: "Event", sub: "what fired · who it was for", w: "minmax(200px,1.3fr)" },
    { key: "to", label: "To", w: "minmax(200px,1.2fr)" },
    { key: "shop", label: "Shop / ref", w: "minmax(160px,1fr)" },
    { key: "status", label: "Status", sub: "sent · failed · queued", w: "minmax(180px,1fr)" },
    { key: "actions", label: "Actions", sub: "retry", w: "80px" },
];
const LOG_STATUS = [["", "Every status"], ["sent", "Sent"], ["failed", "Failed"], ["queued", "Queued"]];

function Health({ health, counts, onFlush, busy }) {
    const ok = health.connected;
    return (
        <Section testid="mail-health" title={<span className="inline-flex items-center gap-1.5">{ok ? <CheckCircle2 size={13} className="text-[#067647]" /> : <AlertTriangle size={13} className="text-[#b54708]" />} {health.provider} · {ok ? "connected" : "not connected"}</span>}
            note={health.note} pad={false}
            right={(counts.queued || counts.failed) ? (
                <Btn onClick={onFlush} disabled={busy} testid="mail-flush">
                    {busy ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />} send everything that's waiting
                </Btn>
            ) : null}>
            <Tiles items={[
                ["Sends from", health.from || "— not set —", health.missing_env?.length ? `missing: ${health.missing_env.join(", ")}` : "the verified sender"],
                ["Sent", counts.sent || 0, "delivered by the provider"],
                ["Failed", counts.failed || 0, "the provider said no"],
                ["Queued", counts.queued || 0, "waiting on a key or a retry"],
            ]} />
        </Section>
    );
}

function Inboxes({ shops, ownerInbox, onSaved }) {
    const [drafts, setDrafts] = useState({});
    const [owner, setOwner] = useState(ownerInbox || "");
    const [busy, setBusy] = useState(null);

    const save = async (slug) => {
        setBusy(slug);
        try {
            const { data } = await api.put(`/platform/notifications/inbox/${slug}`, { notification_email: drafts[slug] ?? "" });
            toast.success(`${data.shop.name} now delivers to ${data.delivers_to}`);
            onSaved();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(null); }
    };
    const saveOwner = async () => {
        setBusy("__owner");
        try {
            const { data } = await api.put("/platform/notifications/owner-inbox", { owner_email: owner });
            toast.success(`Platform admin inbox is now ${data.owner_inbox}`);
            onSaved();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(null); }
    };

    return (
        <Section title="Notification emails" pad={false} testid="admin-inboxes" note="change any of these without a code edit · blank falls back to the shop owner's login">
            <FieldRow label="Platform office (you)" hint="where hallway mail and every office copy lands">
                <div className="flex flex-wrap items-center gap-1.5">
                    <input className={`${inputCls} max-w-[360px]`} value={owner} onChange={(e) => setOwner(e.target.value)} data-testid="owner-inbox-input" />
                    <Btn tone="solid" onClick={saveOwner} disabled={busy === "__owner"} testid="owner-inbox-save">
                        {busy === "__owner" ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />} Save
                    </Btn>
                </div>
            </FieldRow>
            <div className="border-t border-[#d0d0d0]">
                <Sheet cols={INBOX_COLS} rows={shops} rowKey={(s) => s.slug} testid="inboxes-sheet" minWidth={820}
                    rowTestid={(s) => `inbox-row-${s.slug}`} empty="No shops yet."
                    cells={(s) => {
                        const value = drafts[s.slug] ?? (s.notification_email || "");
                        return {
                            shop: <Cell><Two a={s.name} b={`${s.tier} · ${s.status}`} /></Cell>,
                            login: <Cell><Txt sub>{s.owner_email}</Txt></Cell>,
                            email: (
                                <Cell>
                                    <input className={`${cellCls} w-full text-left`} placeholder={s.owner_email} value={value}
                                        onChange={(e) => setDrafts((d) => ({ ...d, [s.slug]: e.target.value }))} data-testid={`inbox-input-${s.slug}`} />
                                </Cell>
                            ),
                            actions: (
                                <Cell className="justify-end">
                                    <Btn tone="solid" onClick={() => save(s.slug)} disabled={busy === s.slug} testid={`inbox-save-${s.slug}`}>
                                        {busy === s.slug ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />} Save
                                    </Btn>
                                </Cell>
                            ),
                        };
                    }} />
            </div>
        </Section>
    );
}

export default function NotificationsAdmin() {
    const [data, setData] = useState(null);
    const [status, setStatus] = useState("");
    const [event, setEvent] = useState("");
    const [store, setStore] = useState("");
    const [busy, setBusy] = useState(false);

    const load = useCallback(() => {
        const params = {};
        if (status) params.status = status;
        if (event) params.event = event;
        if (store) params.store = store;
        api.get("/platform/notifications", { params }).then(({ data }) => setData(data)).catch(() => setData(null));
    }, [status, event, store]);
    useEffect(() => { load(); }, [load]);

    const retry = async (id) => {
        try {
            const { data: res } = await api.post(`/platform/notifications/${id}/retry`);
            toast[res.sent ? "success" : "error"](res.sent ? "Sent" : res.reason || "Still failing");
            load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    const flush = async () => {
        setBusy(true);
        try { const { data: res } = await api.post("/platform/notifications/flush"); toast.success(`${res.sent} of ${res.tried} went out`); load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    const digest = async () => {
        setBusy(true);
        try {
            const { data: res } = await api.post("/platform/notifications/digest");
            toast[res.sent ? "success" : "error"](res.sent ? `Digest sent — ${res.headline}` : res.reason || "Digest failed");
            load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    if (!data) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#4a4a4a]" /></div>;
    const guide = data.subject_guide;

    return (
        <div className="space-y-3" data-testid="admin-notifications">
            <Health health={data.health} counts={data.counts} onFlush={flush} busy={busy} />
            <DropoffCode />

            <Section title="Your morning digest" testid="digest-panel" pad={false}
                note={`one email with every order, application, message and approval across all ${data.shops.length} shops — 8am Brooklyn time, every day`}
                right={
                    <Btn tone="solid" onClick={digest} disabled={busy} testid="digest-send-now">
                        {busy ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />} Send it now
                    </Btn>
                }>
                {data.digests?.length ? (
                    <Sheet cols={[
                        { key: "day", label: "Day", w: "130px" },
                        { key: "headline", label: "Headline", w: "minmax(260px,2fr)" },
                        { key: "money", label: "Money", sub: "$ booked that day", w: "110px" },
                        { key: "status", label: "Status", w: "110px" },
                    ]} rows={data.digests} rowKey={(d) => d.id} testid="digest-history" minWidth={640}
                        cells={(d) => ({
                            day: <Cell><Txt bold>{d.day}</Txt></Cell>,
                            headline: <Cell><Txt>{d.headline}</Txt></Cell>,
                            money: <Cell><Txt mono right>${Number(d.money || 0).toFixed(2)}</Txt></Cell>,
                            status: <Cell><Tag status={d.status} /></Cell>,
                        })} />
                ) : <p className="px-2.5 py-3 text-sm text-[#4a4a4a]">No digest has gone out yet.</p>}
            </Section>

            <Section title="Where things go" pad={false} testid="admin-help-routing"
                note={`plain English · the 13thav support inbox is ${data.owner_inbox || "— not set —"} · a shop's own notification email wins, then the owner's login, then you`}>
                <Sheet cols={ROUTE_COLS} rows={data.routing} rowKey={(r) => r.action} testid="routing-sheet" minWidth={960}
                    cells={(r) => ({
                        action: <Cell><Txt bold title={r.action}>{r.action}</Txt></Cell>,
                        destination: <Cell><Txt title={r.destination}>{r.destination}</Txt></Cell>,
                        customer: <Cell><Txt sub title={r.customer}>{r.customer}</Txt></Cell>,
                        owner: <Cell><Txt sub title={r.owner}>{r.owner}</Txt></Cell>,
                        manage: <Cell><Txt sub title={r.manage}>{r.manage}</Txt></Cell>,
                    })} />
            </Section>

            {guide && (
                <Section title="Every subject line" pad={false} testid="subject-guide" note={guide.note}>
                    <FieldRow label="Example" hint="what lands in the inbox"><p className="break-words font-mono text-[12px] text-[#1a1a1a]">{guide.example}</p></FieldRow>
                    <FieldRow label="Shape" hint="how it's built"><p className="font-mono text-[12px] text-[#4a4a4a]">{guide.shape}</p></FieldRow>
                    <FieldRow label="Shop codes" hint="the first block" top>
                        <ul className="space-y-0.5 font-mono text-[12px] text-[#1a1a1a]">
                            <li>{guide.mall_code} — The Mall (13thav support)</li>
                            {guide.shops.filter((s) => s.code).map((s) => <li key={s.name}>{s.code} — {s.name}</li>)}
                        </ul>
                    </FieldRow>
                    <FieldRow label="Area codes" hint="the second block" top>
                        <ul className="space-y-0.5 font-mono text-[12px] text-[#1a1a1a]">
                            {guide.areas.map(([code, form]) => <li key={code}>{code} — {form}</li>)}
                        </ul>
                    </FieldRow>
                </Section>
            )}

            <Inboxes shops={data.shops} ownerInbox={data.owner_inbox} onSaved={load} />

            <Section title="Email log" pad={false} testid="email-log-section" note="every send, before it leaves and after the provider answers"
                right={<Btn onClick={load} testid="log-refresh"><RefreshCw size={11} /> refresh</Btn>}>
                <div className="space-y-1.5 border-b border-[#d0d0d0] px-2.5 py-2">
                    <PillRow label="Status" testid="log-filter-status" value={status} onPick={setStatus}
                        choices={LOG_STATUS.map(([key, label]) => ({ key, label, count: key ? data.counts?.[key] : undefined }))} />
                    <div className="flex flex-wrap items-center gap-1.5">
                        <span className="mr-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#4a4a4a]">Narrow</span>
                        <select className={`${cellCls} py-1 text-left`} value={event} onChange={(e) => setEvent(e.target.value)} data-testid="log-filter-event">
                            <option value="">Every event</option>
                            {data.events.map((e) => <option key={e.key} value={e.key}>{e.label}</option>)}
                        </select>
                        <select className={`${cellCls} py-1 text-left`} value={store} onChange={(e) => setStore(e.target.value)} data-testid="log-filter-store">
                            <option value="">Every shop</option>
                            {data.shops.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
                        </select>
                    </div>
                </div>
                <Sheet cols={LOG_COLS} rows={data.log} rowKey={(r) => r.id} testid="email-log" minWidth={1000}
                    rowTestid={(r) => `log-row-${r.id}`} empty="Nothing logged for this filter yet."
                    cells={(r) => ({
                        when: <Cell><Txt sub>{when(r.created_at)}</Txt></Cell>,
                        event: (
                            <Cell>
                                <Two a={<>{r.event_label} <span className="font-normal text-[#4a4a4a]">· {r.role}</span></>}
                                    b={r.code ? <span data-testid={`log-code-${r.id}`}>#{r.code}</span> : null} />
                            </Cell>
                        ),
                        to: <Cell>{r.to ? <Txt>{r.to}</Txt> : <Txt cls="text-[#b42318]">no address</Txt>}</Cell>,
                        shop: <Cell><Txt sub>{[r.store_name || "—", r.ref].filter(Boolean).join(" · ")}</Txt></Cell>,
                        status: (
                            <Cell className="flex-wrap">
                                <Tag status={r.status} testid={`log-status-${r.id}`} />
                                {r.reason && <Txt sub title={r.reason}>{r.reason}</Txt>}
                            </Cell>
                        ),
                        actions: (
                            <Cell className="justify-end">
                                {r.status !== "sent" ? <Btn onClick={() => retry(r.id)} testid={`log-retry-${r.id}`}>retry</Btn> : <Dash why="Already delivered" />}
                            </Cell>
                        ),
                    })} />
                {!data.log.length && <p className="sr-only" data-testid="email-log-empty">Nothing logged for this filter yet.</p>}
            </Section>
        </div>
    );
}
